import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  MessageSquare, ChevronLeft, Plus, Pin, Lock,
  Loader2, Eye, Clock
} from 'lucide-react';
import { useApi } from '../hooks/useApi';
import { useAuth } from '../context/AuthContext';

interface Thread {
  id: string;
  title: string;
  content: string;
  author_name: string;
  author_id: string;
  created_at: string;
  is_pinned: boolean;
  is_locked: boolean;
  view_count: number;
  reply_count: number;
  last_activity: string | null;
}

interface ForumData {
  id: string;
  name: string;
  description: string;
}

interface Pagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

export default function Forum() {
  const { forumId } = useParams<{ forumId: string }>();
  const { user } = useAuth();
  const { fetchApi } = useApi();
  const [forum, setForum] = useState<ForumData | null>(null);
  const [threads, setThreads] = useState<Thread[]>([]);
  const [pagination, setPagination] = useState<Pagination | null>(null);
  const [loading, setLoading] = useState(true);
  const [showNewThread, setShowNewThread] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newContent, setNewContent] = useState('');
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadThreads(1);
  }, [forumId]);

  async function loadThreads(page: number) {
    try {
      setLoading(true);
      const data = await fetchApi<{
        forum: ForumData;
        threads: Thread[];
        pagination: Pagination;
      }>(`/api/discussions/forums/${forumId}/threads?page=${page}`);
      setForum(data.forum);
      setThreads(data.threads);
      setPagination(data.pagination);
    } catch (err) {
      setError('Failed to load forum');
    } finally {
      setLoading(false);
    }
  }

  async function handleCreateThread(e: React.FormEvent) {
    e.preventDefault();
    if (!newTitle.trim() || !newContent.trim()) return;

    setCreating(true);
    setError(null);

    try {
      await fetchApi(`/api/discussions/forums/${forumId}/threads`, {
        method: 'POST',
        body: JSON.stringify({ title: newTitle, content: newContent })
      });
      setNewTitle('');
      setNewContent('');
      setShowNewThread(false);
      loadThreads(1);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create thread');
    } finally {
      setCreating(false);
    }
  }

  function formatTimeAgo(dateString: string) {
    const date = new Date(dateString);
    const now = new Date();
    const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (seconds < 60) return 'just now';
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`;
    return date.toLocaleDateString();
  }

  if (loading && !forum) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-accent-blue" />
      </div>
    );
  }

  if (error && !forum) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-6 text-center">
          <p className="text-red-400">{error}</p>
          <Link to="/community" className="text-accent-blue hover:underline mt-2 inline-block">
            ← Back to Community
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <Link
        to="/community"
        className="inline-flex items-center gap-1 text-gray-400 hover:text-white mb-6"
      >
        <ChevronLeft className="w-4 h-4" />
        Back to Community
      </Link>

      {/* Forum Header */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white">{forum?.name}</h1>
          <p className="text-gray-400 mt-1">{forum?.description}</p>
        </div>
        {user && (
          <button
            onClick={() => setShowNewThread(!showNewThread)}
            className="flex items-center gap-2 px-4 py-2 bg-accent-blue hover:bg-blue-500 text-slate-900 rounded-lg transition-colors"
          >
            <Plus className="w-4 h-4" />
            New Thread
          </button>
        )}
      </div>

      {/* New Thread Form */}
      {showNewThread && (
        <form
          onSubmit={handleCreateThread}
          className="bg-gray-800/50 rounded-lg p-6 mb-6"
        >
          <h3 className="text-lg font-semibold text-white mb-4">Create New Thread</h3>

          <div className="mb-4">
            <label className="block text-sm text-gray-400 mb-1">Title</label>
            <input
              type="text"
              value={newTitle}
              onChange={(e) => setNewTitle(e.target.value)}
              placeholder="Thread title..."
              maxLength={200}
              className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:border-accent-blue focus:outline-none"
            />
          </div>

          <div className="mb-4">
            <label className="block text-sm text-gray-400 mb-1">Content</label>
            <textarea
              value={newContent}
              onChange={(e) => setNewContent(e.target.value)}
              placeholder="What would you like to discuss?"
              rows={5}
              className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:border-accent-blue focus:outline-none resize-none"
            />
          </div>

          {error && (
            <p className="text-red-400 text-sm mb-4">{error}</p>
          )}

          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={() => setShowNewThread(false)}
              className="px-4 py-2 text-gray-400 hover:text-white transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={creating || !newTitle.trim() || !newContent.trim()}
              className="px-4 py-2 bg-accent-blue hover:bg-blue-500 disabled:bg-gray-600 disabled:cursor-not-allowed text-slate-900 rounded-lg transition-colors"
            >
              {creating ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                'Create Thread'
              )}
            </button>
          </div>
        </form>
      )}

      {/* Threads List */}
      {threads.length === 0 ? (
        <div className="bg-gray-800/50 rounded-lg p-12 text-center text-gray-400">
          <MessageSquare className="w-12 h-12 mx-auto mb-4 opacity-50" />
          <p>No threads yet.</p>
          {user && <p className="text-sm mt-1">Start the conversation!</p>}
        </div>
      ) : (
        <div className="space-y-2">
          {threads.map(thread => (
            <Link
              key={thread.id}
              to={`/threads/${thread.id}`}
              className="block bg-gray-800/50 rounded-lg p-4 hover:bg-gray-800 transition-colors"
            >
              <div className="flex items-start gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    {thread.is_pinned && (
                      <Pin className="w-4 h-4 text-accent-blue flex-shrink-0" />
                    )}
                    {thread.is_locked && (
                      <Lock className="w-4 h-4 text-yellow-500 flex-shrink-0" />
                    )}
                    <h3 className="text-white font-medium truncate">
                      {thread.title}
                    </h3>
                  </div>
                  <p className="text-sm text-gray-400 line-clamp-1 mb-2">
                    {thread.content}
                  </p>
                  <div className="flex items-center gap-4 text-xs text-gray-500">
                    <span>{thread.author_name}</span>
                    <span className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {formatTimeAgo(thread.created_at)}
                    </span>
                  </div>
                </div>
                <div className="flex flex-col items-end gap-1 text-sm text-gray-500">
                  <span className="flex items-center gap-1">
                    <MessageSquare className="w-4 h-4" />
                    {thread.reply_count}
                  </span>
                  <span className="flex items-center gap-1">
                    <Eye className="w-4 h-4" />
                    {thread.view_count}
                  </span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}

      {/* Pagination */}
      {pagination && pagination.totalPages > 1 && (
        <div className="flex justify-center gap-2 mt-6">
          {Array.from({ length: pagination.totalPages }, (_, i) => i + 1).map(page => (
            <button
              key={page}
              onClick={() => loadThreads(page)}
              className={`
                w-8 h-8 rounded-lg transition-colors
                ${page === pagination.page
                  ? 'bg-accent-blue text-slate-900'
                  : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
                }
              `}
            >
              {page}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
